import type { MediaManagerTemplate, StageRenderTarget } from 'modules/manager/domain/interfaces';
import { TemplateType } from 'modules/manager/domain/template-type';
import type { RenderTemplateArgs } from 'modules/manager/store/render-template-api';

/**
 * Собирает параметры серверного рендера (`POST /api/render-template`) для шаблона,
 * открытого в редакторе, подставляя фикстурные id. Питает локальную кнопку «Рендер»
 * в шапке — в продукте рендер пока ниоткуда не запускается человеком, и кнопка
 * существует ровно для того, чтобы разработчик не собирал тело запроса руками.
 *
 * Id ниже — строки TEST-базы, захардкожены сознательно: кнопка закрыта `useIsLocal`
 * (см. editor-header.tsx), а это компайл-тайм-константа в любом собранном бандле, так
 * что на задеплоенном окружении кнопка не нарисуется ни у кого. Считайте id одноразовыми
 * фикстурами — здесь нет ни одного контракта, перенацеливайте свободно.
 */

/**
 * Чемпионат, его плоский этап и матчи — из одного соревнования TEST-стенда: чемпионат 1912
 * и его этап 2012. Держите их согласованными: режимы контекста рендера разные, а данные за
 * ними должны описывать одно и то же соревнование, иначе таблица и топ игроков покажут
 * чужие команды.
 */
const FIXTURE_CHAMPIONSHIP_ID = 1912;
const FIXTURE_MATCH_IDS = [63040, 63039, 63038, 63037, 62964, 62946];

const FIXTURE_STAGE_IDS = {
  /** Плоская таблица этапа чемпионата 1912 — одна картинка на этап. */
  flat: 2012,
  /**
   * Групповой этап (`StageType.GROUP`) — добавлен как фикстура для группового рендера:
   * на нём проверяется «по отдельному запросу на каждую группу». Кнопка рендерит его;
   * чтобы посмотреть плоскую таблицу, подставьте `flat` в `PARAM_BUILDERS`.
   */
  group: 1848,
};

/**
 * Новые режимы — из соревнования 792 «Тестовый турнир» (в 1912 нет ни настоящей сетки, ни
 * сборных, ни игроков с датой рождения).
 */
const FIXTURE_NEW_MODES = {
  /** Этап `SINGLE_ELIMINATION` на 16 команд: 4 раунда, 15 узлов, почти все пары не сыграны. */
  bracketStageId: 1195,
  /** Сборная «ав1», схема `1-2-4-2`. Тура у сборных бэкенд пока не хранит. */
  symbolicTeamId: 25908,
  /** Игрок с датой рождения, его команда и соревнование, где она заявлена. */
  birthday: { playerId: 16781, teamId: 3263, championshipId: 792 },
};

/**
 * Позиционно по регионам layout-блоков — `backgrounds[i]` заполняет регион `i`, лишние
 * сверх числа регионов шаблона сервис рендера игнорирует. Трёх хватает на любой пресет.
 *
 * Адреса здесь — только источник байтов: в поле `backgrounds` уезжает **файл**, а не
 * ссылка (см. `downloadFixtureBackgrounds`). Поэтому чужой хост обязан отдавать
 * CORS-заголовки — без них браузер не даст прочитать тело, и фона не будет.
 */
const FIXTURE_BG_URLS = [
  // `w=1080` под разрешение шаблона, а не оригинальные 3000px: в base64 картинка
  // прибавляет треть, и три фона в полный размер уже упираются в лимиты тела запроса.
  'https://images.unsplash.com/photo-1551958219-acbc608c6377?fm=jpg&q=60&w=1080&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1517649763962-0c623066013b?fm=jpg&q=60&w=1080&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?fm=jpg&q=60&w=1080&auto=format&fit=crop',
];

/**
 * По одному сборщику на тип — `Record<TemplateType, …>`, чтобы новый тип шаблона не
 * скомпилировался, пока для него не описан режим контекста рендера.
 *
 * Режим ровно один на запрос: на бэкенде ветки сбора контекста идут подряд без `else`,
 * и лишний id молча меняет выбранный режим (см. `buildRenderTemplateFormData`).
 */
const PARAM_BUILDERS: Record<TemplateType, (template: MediaManagerTemplate) => RenderTemplateArgs> = {
  [TemplateType.SCHEDULE]: (template) => ({ ...common(template), matchId: FIXTURE_MATCH_IDS[0] }),
  [TemplateType.RESULTS]: (template) => ({ ...common(template), matchId: FIXTURE_MATCH_IDS[0] }),
  [TemplateType.MULTIPLE_SCHEDULE]: (template) => ({ ...common(template), matchIds: [...FIXTURE_MATCH_IDS] }),
  [TemplateType.MULTIPLE_RESULTS]: (template) => ({ ...common(template), matchIds: [...FIXTURE_MATCH_IDS] }),
  [TemplateType.TOURNAMENT_TABLE]: (template) => ({ ...common(template), stageId: FIXTURE_STAGE_IDS.group }),
  [TemplateType.TOP_PLAYERS_BY_INDICATORS]: (template) => ({ ...common(template), championshipId: FIXTURE_CHAMPIONSHIP_ID }),
  [TemplateType.PLAYOFF_BRACKET]: (template) => ({ ...common(template), stageId: FIXTURE_NEW_MODES.bracketStageId }),
  // Турнир дня рождения — только внутри `birthday`: корневой `championshipId` включил бы режим статистики.
  [TemplateType.BIRTHDAY]: (template) => ({ ...common(template), birthday: { ...FIXTURE_NEW_MODES.birthday } }),
  [TemplateType.SYMBOLIC_TEAM_ROUND]: (template) => ({ ...common(template), symbolicTeamId: FIXTURE_NEW_MODES.symbolicTeamId }),
  [TemplateType.SYMBOLIC_TEAM_CHAMPIONSHIP]: (template) => ({
    ...common(template),
    symbolicTeamId: FIXTURE_NEW_MODES.symbolicTeamId,
  }),
};

/** Общая часть любого режима: сам шаблон и его подтипы. Фоны добавляются отдельно — они асинхронные. */
function common(template: MediaManagerTemplate): RenderTemplateArgs {
  return {
    templateId: template.id,
    templateSubtypes: [...template.templateSubtypes],
  };
}

/**
 * Скачивает фикстурные фоны и отдаёт их блобами — в поле `backgrounds` уезжают файлы.
 *
 * Недоступный фон просто выпадает из списка: контракт бэкенда — массив файлов, и
 * «пустого» файла в нём не выразить. Ошибку глушим: фон декоративен, картинка без него
 * осмысленна (ровно так же на неё смотрит и сервис рендера).
 */
async function downloadFixtureBackgrounds(signal?: AbortSignal): Promise<Blob[]> {
  const downloaded = await Promise.all(
    FIXTURE_BG_URLS.map(async (url) => {
      try {
        const response = await fetch(url, { signal });

        if (!response.ok) {
          throw new Error(url + ' → HTTP ' + response.status);
        }

        return await response.blob();
      } catch (error) {
        console.warn('[dev-render] фон не скачался, регион останется пустым:', url, error);
        return null;
      }
    }),
  );

  return downloaded.filter((blob): blob is Blob => blob !== null);
}

export async function buildDevRenderParams(
  template: MediaManagerTemplate,
  signal?: AbortSignal,
): Promise<RenderTemplateArgs> {
  const args = PARAM_BUILDERS[template.templateType](template);

  return { ...args, backgrounds: await downloadFixtureBackgrounds(signal) };
}

/** Один запрос рендера и подпись картинки, которую он вернёт. */
export interface DevRenderRequest {
  /** Подпись в превью и хвост имени файла; у одиночной картинки её нет. */
  label?: string;
  args: RenderTemplateArgs;
}

/**
 * Разворачивает запрос по целям этапа: бэкенд на один запрос отдаёт один PNG, поэтому
 * групповой этап — это отдельный запрос с `groupId` на каждую группу. Запрос не в режиме
 * этапа (или этап без групп — одна цель без `groupId`) остаётся одним запросом как есть.
 */
export function splitByStageTargets(args: RenderTemplateArgs, targets: readonly StageRenderTarget[]): DevRenderRequest[] {
  if (args.stageId === undefined || targets.length === 0) {
    return [{ args }];
  }

  return targets.map((target) =>
    target.groupId === undefined ? { args } : { label: target.groupName, args: { ...args, groupId: target.groupId } },
  );
}
